import React, { Fragment, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { successMessages } from '@/constants/feedbackMessages'
import {
	BreadCrumb,
	Button,
	Input,
	Loader,
	ModalBase,
	Paragraph,
	SelectMove,
	Small,
	Title,
} from '@components/atoms'
import { FormNewNotebookValue } from '@components/atoms/QuestionNotebook'
import SearchInput from '@components/atoms/SearchInput'
import {
	Add16Filled,
	Book20Regular,
	BookExclamationMark20Regular,
	Delete20Regular,
	Edit20Regular,
	Folder20Filled,
	Link20Regular,
	MoreVertical16Regular,
} from '@fluentui/react-icons'
import { Menu, Transition } from '@headlessui/react'
import { handleShareLink } from '@helpers'
import { yupResolver } from '@hookform/resolvers/yup'
import {
	EditNotebookRequest,
	FolderNotebookProps,
	NotebookToFolderRequest,
} from '@models/notebook.model'
import { notebookInFolderClientSchema } from '@schemas/notebooks/notebooks.schema'
import { newNotebookSchema } from '@schemas/questions/questions.schema'
import { notebookAPI } from '@services/NotebookAPI'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'

import { NotebooksStyles } from '../style'

const {
	container,
	title,
	folderCard,
	iconFolder,
	containerTitleFolder,
	iconDesc,
	buttonDropdown,
	dropdownItems,
	dropdownItem,
	containerNotebooks,
} = NotebooksStyles

export const NotebookFolderShow = () => {
	const [openModalNotebook, setOpenModalNotebook] = useState(false)
	const [openModalEditlNotebook, setOpenModalEditlNotebook] = useState(false)
	const [openModalDeleteNotebook, setOpenModalDeleteNotebook] = useState(false)
	const [openModalNotebookInFolder, setOpenModalNotebookInFolder] =
		useState(false)
	const [deleteNotebook, setDeleteNotebook] = useState('')
	const [allNotebook, setAllNotebook] = useState<Array<FolderNotebookProps>>()
	const [search, setSearch] = useState<string>('')

	const {
		register: registerNewNotebook,
		reset: resetNewNotebook,
		handleSubmit: handleSubmitNewNotebook,
		formState: { errors: errorsNewNotebook },
	} = useForm<FormNewNotebookValue>({
		resolver: yupResolver(newNotebookSchema),
	})

	const {
		register: registerEditNotebook,
		reset: resetEditNotebook,
		setValue: setValueEditNotebook,
		handleSubmit: handleSubmitEditNotebook,
		formState: { errors: errorsEditNotebook },
	} = useForm<EditNotebookRequest>({
		resolver: yupResolver(newNotebookSchema),
	})

	const {
		reset: resetNotebookInFolder,
		setValue: setValueNotebookInFolder,
		handleSubmit: handleSubmitNotebookInFolder,
		watch: watchNotebookInFolder,
	} = useForm<NotebookToFolderRequest>({
		resolver: yupResolver(notebookInFolderClientSchema),
	})

	const dispatch = useAppDispatch()
	const navigation = useNavigate()

	const notebookInFolderID = watchNotebookInFolder('folderId')

	const [getFolderShowNotebook, { data, isSuccess, isLoading }] =
		notebookAPI.useGetFolderShowNotebooksMutation()
	const [editNotebook, { isSuccess: isSuccessEditNotebook }] =
		notebookAPI.usePutEditNotebookMutation()
	const [deleteNotebookAPI, { isSuccess: isSuccessDeleteNotebook }] =
		notebookAPI.useDeleteNotebookMutation()
	const [getAllFolderNotebook, { data: dataAllFolder }] =
		notebookAPI.useGetAllFolderNotebooksMutation()
	const [postNotebookInFolder, { isSuccess: isSuccessNotebookInFolder }] =
		notebookAPI.usePostNotebookInFolderMutation()
	const [createNotebook, { isSuccess: isSuccessCreateNotebook }] =
		notebookAPI.usePostCreateNotebookInFolderMutation()

	const { id: idFolder } = useParams()

	const filteredAllNotebook =
		allNotebook &&
		allNotebook.filter((option) =>
			option.name.toLowerCase().includes(search.toLowerCase())
		)

	const onSubmitNewBook = handleSubmitNewNotebook((payload) => {
		createNotebook({ name: payload.name, idFolder })
	})

	const onSubmitEditBook = handleSubmitEditNotebook((payload) => {
		editNotebook(payload)
	})

	const onSubmitNotebookInFolder = handleSubmitNotebookInFolder((payload) => {
		postNotebookInFolder(payload)
	})

	const handleDeleteNotebook = () => {
		if (deleteNotebook) {
			deleteNotebookAPI(deleteNotebook)
		}
	}

	useEffect(() => {
		getFolderShowNotebook(idFolder || '')
		getAllFolderNotebook()
	}, [getAllFolderNotebook, getFolderShowNotebook, idFolder])

	useEffect(() => {
		if (isSuccess && data) {
			setAllNotebook(data.notebooks)
		}
	}, [isSuccess, data])

	useEffect(() => {
		if (isSuccessEditNotebook) {
			getFolderShowNotebook(idFolder || '')
			resetEditNotebook()
			setOpenModalEditlNotebook(false)
			dispatch(notificationCreators.show(successMessages.editedNotebook))
		}
	}, [
		dispatch,
		getFolderShowNotebook,
		idFolder,
		isSuccessEditNotebook,
		resetEditNotebook,
	])

	useEffect(() => {
		if (isSuccessDeleteNotebook) {
			getFolderShowNotebook(idFolder || '')
			getAllFolderNotebook()
			setOpenModalDeleteNotebook(false)
			setDeleteNotebook('')
			dispatch(notificationCreators.show(successMessages.deletedNotebook))
		}
	}, [
		dispatch,
		getAllFolderNotebook,
		getFolderShowNotebook,
		idFolder,
		isSuccessDeleteNotebook,
		resetEditNotebook,
	])

	useEffect(() => {
		if (isSuccessNotebookInFolder) {
			getFolderShowNotebook(idFolder || '')
			getAllFolderNotebook()
			setOpenModalNotebookInFolder(false)
			resetNotebookInFolder()
			dispatch(notificationCreators.show(successMessages.moveNotebook))
		}
	}, [
		dispatch,
		getAllFolderNotebook,
		getFolderShowNotebook,
		idFolder,
		isSuccessNotebookInFolder,
		resetNotebookInFolder,
		setOpenModalNotebookInFolder,
	])

	useEffect(() => {
		if (isSuccessCreateNotebook) {
			getFolderShowNotebook(idFolder || '')
			resetNewNotebook()
			setOpenModalNotebook(false)
			dispatch(notificationCreators.show(successMessages.createdNotebook))
		}
	}, [
		dispatch,
		getFolderShowNotebook,
		isSuccessCreateNotebook,
		resetNewNotebook,
		idFolder,
	])

	return (
		<div className={container()}>
			<div>
				<BreadCrumb
					buttonReturn
					locations={
						data?.name
							? [
									{ name: 'Cadernos', link: '/cadernos' },
									{ name: `Pasta ${data?.name}`, link: '' },
							  ]
							: []
					}
				/>
				<div className={title()}>
					<Title variant='h1' color='$neutral160'>
						{data?.name || ''}
					</Title>
					<div>
						<SearchInput
							placeholder='Palavra chave'
							onChange={(e) => setSearch(e.target.value)}
							maxLength={30}
							value={search}
							css={{ maxWidth: '100%', '@md': { maxWidth: '287px' } }}
						/>

						<div className={buttonDropdown()}>
							<Button
								css={{ minWidth: '172px', gap: '5px' }}
								onClick={() => {
									setOpenModalNotebook(!openModalNotebook)
								}}
							>
								Criar caderno <Add16Filled />
							</Button>
						</div>
					</div>
				</div>
			</div>

			<Title variant='h2' color='$neutral110'>
				Cadernos
			</Title>

			<div className={containerNotebooks()}>
				{isLoading ? (
					<Loader />
				) : (
					filteredAllNotebook?.map(({ name, id, questions, date }, key) => {
						return (
							<div className={folderCard()} key={key}>
								<Link to={`/cadernos/${id}`} className={iconDesc()}>
									<div className={iconFolder()}>
										<Book20Regular />
									</div>
									<div className={containerTitleFolder()}>
										<Paragraph
											paragraphType='p1'
											css={{ color: '$neutral100' }}
										>
											{name}
										</Paragraph>

										<Small smallType='small1' css={{ color: '$neutral90' }}>
											Criado em {date} | {questions} questões
										</Small>
									</div>
								</Link>

								<Menu as='div'>
									<div className={buttonDropdown()}>
										<Menu.Button>
											<MoreVertical16Regular />
										</Menu.Button>
									</div>
									<Transition
										as={Fragment}
										enter='transition ease-out duration-100'
										enterFrom='transform opacity-0 scale-95'
										enterTo='transform opacity-100 scale-100'
										leave='transition ease-in duration-75'
										leaveFrom='transform opacity-100 scale-100'
										leaveTo='transform opacity-0 scale-95'
									>
										<Menu.Items className={dropdownItems()}>
											<Menu.Item>
												<button
													type='button'
													className={dropdownItem()}
													onClick={() => {
														navigation(`/cadernos/erros/${id}`)
													}}
												>
													<BookExclamationMark20Regular />
													Caderno de erros
												</button>
											</Menu.Item>
											<Menu.Item>
												<button
													type='button'
													className={dropdownItem()}
													onClick={() => {
														handleShareLink({ type: 'notebook', id })
														dispatch(
															notificationCreators.show(
																successMessages.copyLink
															)
														)
													}}
												>
													<Link20Regular /> Compartilhar
												</button>
											</Menu.Item>
											{/* <Menu.Item>
												<button
													type='button'
													className={dropdownItem()}
													onClick={() => {
														setValueNotebookInFolder('notebookId', id)
														setOpenModalNotebookInFolder(true)
													}}
												>
													<FolderArrowRight20Regular />
													Mover
												</button>
											</Menu.Item> */}
											<Menu.Item>
												<button
													type='button'
													className={dropdownItem()}
													onClick={() => {
														setValueEditNotebook('name', name)
														setValueEditNotebook('id', String(id))
														setOpenModalEditlNotebook(!openModalEditlNotebook)
													}}
												>
													<Edit20Regular /> Renomear
												</button>
											</Menu.Item>
											<Menu.Item>
												<button
													type='button'
													className={dropdownItem()}
													onClick={() => {
														setOpenModalDeleteNotebook(!openModalDeleteNotebook)
														setDeleteNotebook(String(id))
													}}
												>
													<Delete20Regular /> Excluir
												</button>
											</Menu.Item>
										</Menu.Items>
									</Transition>
								</Menu>
							</div>
						)
					})
				)}
			</div>

			<ModalBase
				title='Criar caderno'
				open={openModalNotebook}
				onOpenChange={setOpenModalNotebook}
			>
				<form onSubmit={onSubmitNewBook} style={{ width: '100%' }}>
					<Input
						placeholder='Digite o nome do caderno...'
						error={errorsNewNotebook.name?.message}
						{...registerNewNotebook('name')}
						css={{ marginBottom: '34px', marginTop: '58px' }}
					/>
					<div style={{ display: 'flex', gap: '26px', marginTop: '34px' }}>
						<Button>Salvar</Button>
						<Button
							outlined
							onClick={() => {
								setOpenModalNotebook(!openModalNotebook)
								resetNewNotebook()
							}}
						>
							Cancelar
						</Button>
					</div>
				</form>
			</ModalBase>

			<ModalBase
				title='Renomear caderno'
				open={openModalEditlNotebook}
				onOpenChange={setOpenModalEditlNotebook}
			>
				<form onSubmit={onSubmitEditBook} style={{ width: '100%' }}>
					<Input
						placeholder='Digite o nome do caderno...'
						error={errorsEditNotebook.name?.message}
						{...registerEditNotebook('name')}
						css={{ marginBottom: '34px', marginTop: '58px' }}
					/>
					<div style={{ display: 'flex', gap: '26px', marginTop: '34px' }}>
						<Button>Salvar</Button>
						<Button
							outlined
							onClick={() => {
								setOpenModalEditlNotebook(!openModalEditlNotebook)
								resetEditNotebook()
							}}
						>
							Cancelar
						</Button>
					</div>
				</form>
			</ModalBase>

			<ModalBase
				title='Excluir caderno'
				open={openModalDeleteNotebook}
				onOpenChange={setOpenModalDeleteNotebook}
			>
				<div>
					<Title variant='h4' color='$neutral160'>
						Deseja apagar todo este caderno?
					</Title>
					<div style={{ display: 'flex', gap: '26px', marginTop: '34px' }}>
						<Button onClick={handleDeleteNotebook}>Apagar</Button>
						<Button
							outlined
							onClick={() => {
								setOpenModalDeleteNotebook(!openModalDeleteNotebook)
							}}
						>
							Cancelar
						</Button>
					</div>
				</div>
			</ModalBase>

			<ModalBase
				title='Mover Caderno'
				open={openModalNotebookInFolder}
				onOpenChange={(e) => {
					resetNotebookInFolder()
					setOpenModalNotebookInFolder(e)
				}}
			>
				<div
					style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
				>
					<form
						onSubmit={onSubmitNotebookInFolder}
						style={{
							marginTop: '34px',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<SelectMove
							options={
								(dataAllFolder &&
									dataAllFolder.map((folder) => {
										return { value: folder.id, option: folder.name }
									})) ||
								[]
							}
							onChange={(e) => {
								setValueNotebookInFolder('folderId', Number(e.value))
							}}
						/>

						<div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								gap: '26px',
								marginTop: '17px',
								paddingLeft: '16px',
							}}
						>
							<Folder20Filled />
							{notebookInFolderID ? (
								<Button css={{ maxWidth: '164px' }}>Mover</Button>
							) : (
								<Button disabled css={{ maxWidth: '164px' }}>
									Mover para cá
								</Button>
							)}
						</div>
					</form>
				</div>
			</ModalBase>
		</div>
	)
}
